import React, { useEffect, useState } from "react";
import { ArrowDownIcon } from "lucide-react";
import styles from "./FloatingButtons.module.css";
import { smoothScrollTo, cancelSmoothScroll } from "../utils/smoothScrollTo";

type ScrollDownButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  className?: string;
};

const ScrollDownButton: React.FC<ScrollDownButtonProps> = ({
  className,
  onClick,
  ...rest
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const pageHeight = document.documentElement.scrollHeight;

      const passedHalfPage = scrollY > pageHeight / 2;
      const atBottom = scrollY + viewportHeight >= pageHeight - 100;

      setIsVisible(passedHalfPage && !atBottom);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    if (e.defaultPrevented) return;

    e.currentTarget.blur();

    const reduce =
      typeof document !== "undefined" &&
      document.documentElement.hasAttribute("data-motion-reduce");

    const targetY = document.documentElement.scrollHeight;

    cancelSmoothScroll();
    if (reduce) {
      window.scrollTo(0, targetY);
      return;
    }

    smoothScrollTo(targetY, 1100);
  };

  const cx = [
    styles.floatingButton,
    styles.scrollDownButton,
    !isVisible && styles.hidden,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={cx}
      onClick={handleClick}
      aria-label="Nach unten scrollen"
      title="Weiter"
      {...rest}
    >
      <ArrowDownIcon size={22} aria-hidden="true" focusable="false" />
    </button>
  );
};

export default ScrollDownButton;
